import { useEffect, useState } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { clearAuthHeaders } from "api";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { programDataAtom } from "recoil/programDataAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { onboardingDataState } from "recoil/onboardingDataAtom";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";
import {
  appBiometricEnabledAtom,
  appLockEnabledAtom,
  appLockPinHashAtom,
} from "recoil/appLockAtoms";
import { lastLoginAccountHintAtom } from "recoil/lastLoginAccountHintAtom";
import { loginHintAtom } from "recoil/loginHintAtom";
import { notifyAppLogout } from "utils/webview";
import {
  ACCOUNT_HASH_REGEX,
  shouldShowAccountMismatch,
} from "utils/accountHint";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import useAccountIdHash from "hooks/useAccountIdHash";
import useIsInappWebview from "hooks/useIsInappWebview";
import useFetch from "hooks/useFetch";
import CustomModal from "components/common/CustomModal";
import mixpanel from "mixpanel-browser";

const PROVIDER_LABEL: Record<string, string> = {
  kakao: "Kakao",
  google: "Google",
  apple: "Apple",
};

// 웹퍼널(결제 완료 등)에서 앱으로 넘어온 계정 해시(deep_link_sub2)와
// 앱에 로그인된 계정을 비교해, 다르면 재로그인 안내 모달을 띄운다.
// 웹퍼널에서 이용한 계정과 앱 계정이 달라 온보딩/결제가 다시 노출되는
// (이중 결제 위험) 상황을 막기 위한 가드. RootLayout 에 상시 마운트.
export default function AccountMismatchGuard() {
  const isInappWebview = useIsInappWebview();
  const user = useRecoilValue(userAtom);
  const [hint, setHint] = useRecoilState(lastLoginAccountHintAtom);
  const myAccountHash = useAccountIdHash(user?.userId);
  const message = useGetCertainEventFromApp({
    eventName: "lastLoginAccountHash",
  });
  // 웹퍼널 계정의 provider. 네이티브가 lastLoginAccountHash 직전에 보내는
  // lastLoginMethod 이벤트에서 수신 시점에 캡처해 힌트에 함께 저장한다.
  // (loginHintAtom 은 현재 로그인 계정의 authenticateType 으로 덮이므로
  //  이후 부팅에서 잘못된 계정의 provider 를 안내하게 됨 — 사용 금지)
  const methodMessage = useGetCertainEventFromApp({
    eventName: "lastLoginMethod",
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [, fetchLogout] = useFetch({ url: "/user/logout" });
  const resetUser = useResetRecoilState(userAtom);
  const resetPid = useResetRecoilState(patientAtom);
  const resetProgramData = useResetRecoilState(programDataAtom);
  const resetTaskState = useResetRecoilState(taskListAtom);
  const resetOnboardingData = useResetRecoilState(onboardingDataState);
  const resetHomeThoughtRecordState = useResetRecoilState(
    homeThoughtRecordListAtom
  );
  const setAppLockEnabled = useSetRecoilState(appLockEnabledAtom);
  const setAppBiometricEnabled = useSetRecoilState(appBiometricEnabledAtom);
  const resetAppLockPinHash = useResetRecoilState(appLockPinHashAtom);
  const setLoginHint = useSetRecoilState(loginHintAtom);

  // 네이티브가 전달한 계정 해시 수신 → 영속 저장.
  // clickId(네이티브가 딥링크 도착 시점에 발급)가 저장분과 다를 때만
  // 새 CTA 클릭으로 취급해 receivedAt 을 갱신하고 dismissedHash 를 해제한다
  // (닫기/TTL 이후 재클릭 시 가드 재무장). 웹뷰 ready 마다 오는 재송신은
  // 같은 clickId 라 무시된다.
  useEffect(() => {
    const value = message?.params?.value;
    const clickId = message?.params?.clickId;
    if (typeof value !== "string" || !ACCOUNT_HASH_REGEX.test(value)) return;
    if (typeof clickId !== "string" || !clickId) return;

    const methodValue = methodMessage?.params?.value;
    const provider =
      methodValue === "kakao" ||
      methodValue === "google" ||
      methodValue === "apple"
        ? methodValue
        : undefined;
    setHint((prev) =>
      prev?.clickId === clickId
        ? prev
        : { hash: value, clickId, provider, receivedAt: Date.now() }
    );
  }, [message, methodMessage, setHint]);

  // 해시가 일치하면(정상 계정으로 로그인됨) 힌트 소거
  useEffect(() => {
    if (hint?.hash && myAccountHash && hint.hash === myAccountHash) {
      setHint(undefined);
    }
  }, [hint, myAccountHash, setHint]);

  const open = shouldShowAccountMismatch({
    hint,
    myAccountHash,
    isLoggedIn: !!user?.accessToken,
    isInWebview: !!isInappWebview,
    now: Date.now(),
  });

  const dismiss = () => {
    if (hint) setHint({ ...hint, dismissedHash: hint.hash });
  };

  // AccountPage 의 로그아웃 초기화와 동일한 절차 (재로그인 유도)
  const handleLogoutAndRelogin = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      // 로그인 화면 배지를 모달 안내와 일치시킨다 — loginHintAtom 은 직전(잘못된)
      // 계정의 authenticateType 으로 덮여 있을 수 있다(localStorage 영속이라
      // 아래 리다이렉트 후에도 유지됨)
      if (hint?.provider) setLoginHint(hint.provider);
      await fetchLogout();
      resetUser();
      resetPid();
      resetProgramData();
      resetTaskState();
      resetOnboardingData();
      resetHomeThoughtRecordState();
      setAppLockEnabled(false);
      setAppBiometricEnabled(false);
      resetAppLockPinHash();
      clearAuthHeaders();
      await notifyAppLogout();
      mixpanel.reset();
      window.location.href = `${window.location.origin}/login`;
    } catch {
      setIsLoggingOut(false);
    }
  };

  if (!open) return null;

  const providerLabel = hint?.provider
    ? PROVIDER_LABEL[hint.provider]
    : undefined;
  const subtitle = providerLabel
    ? `웹에서 이용하신 계정과 지금 로그인된 계정이 달라요. 웹에서 결제하셨다면, 로그아웃 후 '${providerLabel}로 시작하기'로 다시 로그인해주세요.`
    : "웹에서 이용하신 계정과 지금 로그인된 계정이 달라요. 웹에서 결제하셨다면, 로그아웃 후 결제하신 계정으로 다시 로그인해주세요.";

  return (
    <CustomModal
      open
      onClose={dismiss}
      title={"다른 계정으로 로그인되어 있어요"}
      subtitle={subtitle}
      actions={[
        {
          label: "로그아웃하고 다시 로그인",
          variant: "Solid",
          onClick: handleLogoutAndRelogin,
          buttonProps: { loading: isLoggingOut },
        },
        {
          label: "닫기",
          variant: "Plain",
          onClick: dismiss,
        },
      ]}
      isMobile={true}
    />
  );
}
